import ExerciseHelpers from "helpers/ExerciseHelpers";
import CircuitHelpers from "helpers/CircuitHelpers";
import type { Metadata } from "types";
import type { MetadataItem } from "types/metadata";
import { MetadataType } from "types";

class MetadataHelpers {
  static getMetadataMetricLabel(attribute: Metadata | null, metricField?: string | null): string | null {
    if (!attribute) return null;

    if (attribute.type === MetadataType.EXERCISE) {
      const { metadata } = attribute;

      const metric = ExerciseHelpers.getMetricByFieldNameOrDefault(metadata, metricField);
      if (!metric) return null;

      return metric.name;
    }

    if (attribute.type === MetadataType.CIRCUIT) {
      const { metadata } = attribute;

      const metric = CircuitHelpers.getMetricByFieldNameOrDefault(metadata, metricField);
      if (!metric) return null;

      return metric.name;
    }

    if (attribute.type === MetadataType.WELLNESS) {
      const { metadata } = attribute;

      return metadata.name;
    }

    if (attribute.type === MetadataType.ANTHROPOMETRIC) {
      const { metadata } = attribute;

      return metadata.label;
    }

    if (attribute.type === MetadataType.SCORE) {
      const { metadata } = attribute;

      return metadata.name;
    }

    return null;
  }

  static getMetadataName(attribute: Metadata | null): string | null {
    if (!attribute) return null;

    if (attribute.type === MetadataType.EXERCISE) {
      const { metadata } = attribute;

      return metadata.name;
    }

    if (attribute.type === MetadataType.CIRCUIT) {
      const { metadata } = attribute;

      return metadata.name;
    }

    if (attribute.type === MetadataType.WELLNESS) {
      const { metadata } = attribute;

      return metadata.name;
    }

    if (attribute.type === MetadataType.ANTHROPOMETRIC) {
      const { metadata } = attribute;

      return metadata.label;
    }

    if (attribute.type === MetadataType.SCORE) {
      const { metadata } = attribute;

      return metadata.name;
    }

    return null;
  }

  static getMetadataCategory(attribute: Metadata | null): string | null {
    if (!attribute) return null;

    if (attribute.type === MetadataType.EXERCISE) {
      const { metadata } = attribute;

      return metadata.category;
    }

    return attribute.typeLabel;
  }

  static getMetadataMetricByField(
    attribute: Metadata | null,
    metricField?: string | null,
    opts?: { excludedMetricFields?: string[] }
  ) {
    if (!attribute) return null;

    if (attribute.type === MetadataType.EXERCISE) {
      const { metadata } = attribute;

      const metric = ExerciseHelpers.getMetricByFieldNameOrDefault(
        {
          ...metadata,
          metrics: opts?.excludedMetricFields?.length
            ? metadata.metrics.filter((m) => !opts.excludedMetricFields!.includes(m.field))
            : metadata.metrics,
        },
        metricField
      );
      if (!metric) return null;

      return metric;
    } else if (attribute.type === MetadataType.CIRCUIT) {
      const { metadata } = attribute;

      const metric = CircuitHelpers.getMetricByFieldNameOrDefault(
        {
          ...metadata,
          metrics: opts?.excludedMetricFields?.length
            ? metadata.metrics.filter((m) => !opts.excludedMetricFields!.includes(m.field))
            : metadata.metrics,
        },
        metricField
      );
      if (!metric) return null;

      return metric;
    } else if (attribute.type === MetadataType.ANTHROPOMETRIC) {
      return attribute.metadata;
    } else if (attribute.type === MetadataType.SCORE) {
      return attribute.metadata;
    }

    return null;
  }

  /**
   * @description Circuit measurements are queried through the same pipeline as exercises —
   * by `exerciseId` (the circuit/exercise id) + `metricId` (the metric field). Use this to
   * branch the aggregated-measure fetch and fetch-type logic so circuits behave like exercises.
   */
  static usesExerciseQueryShape(type: MetadataType): boolean {
    return type === MetadataType.EXERCISE || type === MetadataType.CIRCUIT;
  }

  /**
   * @description The synthetic dashboard 1RM metrics (manual and velocity-based) are attached to exercises,
   * but the aggregate endpoints expect them as `type: "OneRepMax"`.
   */
  static usesOneRepMaxQueryType(metadata: Pick<MetadataItem, "type" | "field">): boolean {
    return (
      metadata.type === MetadataType.EXERCISE &&
      ["weightOneRepMax", "lvpAutomatedOneRepMax"].includes(metadata.field ?? "")
    );
  }

  static hasVariants(attribute: Metadata | null): boolean {
    if (!attribute) return false;

    if (attribute.type === MetadataType.EXERCISE) {
      const { metadata } = attribute;
      return !!metadata.variants.length;
    }
    return false;
  }
}

export default MetadataHelpers;
